/**
 * ProjectSelector Component
 *
 * Shared project selection dropdown with ProjectCard rendering.
 * Used by AddForm and EditForm for hard bookings.
 *
 * @example
 * ```tsx
 * <ProjectSelector
 *   label="Prosjekt"
 *   options={projectOptions}
 *   value={selectedProjectOption}
 *   onChange={handleProjectChange}
 *   loading={projectsLoading}
 *   testId="add-form-project"
 * />
 * ```
 */

import React from "react";
import classNames from "classnames";
import { Select } from "@/components/ui/forms/Select";
import { ProjectCard } from "@/components/ui/display/ProjectCard";
import type { CustomerLike } from "@/components/ui/display/ProjectCard/types";
import type { SelectOption } from "@/components/ui/forms/Select/types";
import styles from "./ProjectSelector.module.css";

export interface ProjectSelectorProps {
  /** Label for the select */
  label: string;
  /** Placeholder text */
  placeholder?: string;
  /** Available project options */
  options: SelectOption<number>[];
  /** Currently selected option */
  value: SelectOption<number>[];
  /** Change handler */
  onChange: (selected: SelectOption<number>[]) => void;
  /** Whether projects are loading */
  loading?: boolean;
  /** Known customers for client name validation */
  knownCustomers?: CustomerLike[];
  /** Test ID for the select */
  testId: string;
  /** Optional additional CSS class */
  className?: string;
}

export const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  label,
  placeholder = "Velg prosjekt...",
  options,
  value,
  onChange,
  loading = false,
  knownCustomers,
  testId,
  className,
}) => {
  return (
    <div className={classNames(styles.projectSelector, className)}>
      <Select
        label={label}
        placeholder={placeholder}
        options={options}
        value={value}
        onChange={onChange}
        searchable
        disabled={loading}
        renderOption={(option, renderState) => (
          <ProjectCard
            code={String(option.id)}
            name={option.label}
            knownCustomers={knownCustomers}
            variant="default"
            selected={renderState.selected}
            disabled={renderState.disabled}
          />
        )}
        data-testid={testId}
      />
    </div>
  );
};

ProjectSelector.displayName = "ProjectSelector";

export default ProjectSelector;
